import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2, Mail, User } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AddTeacherFormProps {
  onSubmit: (firstname: string, lastname: string, email: string, isInvite: boolean, bg_color_agenda: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const AddTeacherForm: React.FC<AddTeacherFormProps> = ({ onSubmit, onCancel, isSubmitting = false }) => {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [bg_color_agenda, setColor] = useState('');
  const [isInvite, setIsInvite] = useState(false);
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstname.trim() && lastname.trim()) {
      onSubmit(firstname.trim(), lastname.trim(), email.trim(), isInvite, bg_color_agenda);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-grow mb-5 mt-5">
        <div className="flex items-center gap-4 bg-gray-50 p-4 mb-3 rounded-md">
            {/* L'input à gauche */}
            <div className="shrink-0">
              <Input
                id="edit-category-color"
                type="color"
                value={bg_color_agenda}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 p-1 rounded-md cursor-pointer"
              />
            </div>

            {/* Tout le texte à droite */}
            <div className="flex flex-col justify-center">
              <Label htmlFor="edit-category-color" className="mb-0 cursor-pointer">
                {t('categories.color')}
              </Label>
              
              {!bg_color_agenda && (
                <span className="text-xs text-muted-foreground leading-tight">
                  Personnalisez la couleur d'affichage des événements de ce professeur.
                </span>
              )}
            </div>
          </div>
        <div>
          <Label htmlFor="new-teacher-firstname">{t('teachers.firstName')}</Label>
          <Input
            id="new-teacher-firstname"
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            placeholder={t('teachers.firstNamePlaceholder')}
            autoFocus
          />
        </div>
        <div className="mt-4">
          <Label htmlFor="new-teacher-lastname">{t('teachers.lastName')}</Label>
          <Input
            id="new-teacher-lastname"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            placeholder={t('teachers.lastNamePlaceholder')}
          />
        </div>
        <div className="mt-4">
          <Label htmlFor="new-teacher-email">{t('teachers.email')}</Label>
          <Input
            id="new-teacher-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('teachers.emailPlaceholder')}
          />
        </div>
        <div className="mt-4 flex items-center space-x-2">
          <Checkbox
            id="is-invite"
            checked={isInvite}
            onCheckedChange={(checked) => setIsInvite(checked as boolean)}
            disabled={!email.trim()}
          />
          <Label htmlFor="is-invite">{t('teachers.sendInvite')}</Label>
        </div>
      </div>
      <div className={`flex gap-2 pt-4 mt-4 border-t ${
        isMobile
          ? 'fixed bottom-0 left-0 right-0 bg-gray-50 p-4 z-50 justify-center'
          : 'justify-end'
      }`}>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!firstname.trim() || !lastname.trim() || isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center">
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {t('common.processing')}
            </span>
          ) : (
            t('common.done')
          )}
        </Button>
      </div>
    </form>
  );
};

export default AddTeacherForm;
